import { LICENSE_TYPE_LABELS, LicenseType } from './school-license.model';

/**
 * The features the server sells per tier, mirroring its endpoint gates. Everything not named here
 * — every read, every ordinary write, every one-shot AI generation — comes with any active
 * license, so a screen only asks about a feature that is actually restricted.
 */
export type LicenseFeature =
  | 'blockChat'
  | 'inviteExisting'
  | 'dataExport'
  | 'svpDocument'
  | 'coverTemplate'
  | 'basicTraining'
  | 'advancedTraining'
  | 'diaryCourse'
  | 'aiSettings';

/**
 * Which tiers each feature is sold with, as a *set*: the tiers are not a ladder. ZKUŠEBNÍ reaches
 * the chat and the existing-account invite that START does not, and loses the ŠVP document that
 * START has — so no comparison of tiers can stand in for a lookup here.
 *
 * Listed cheapest first: the order is what the tooltips read out. Every feature includes PREMIUM.
 */
export const FEATURE_TIERS: Record<LicenseFeature, readonly LicenseType[]> = {
  blockChat: ['free_trial', 'premium'],
  inviteExisting: ['free_trial', 'premium'],
  dataExport: ['premium'],
  svpDocument: ['start', 'premium'],
  coverTemplate: ['premium'],
  basicTraining: ['free_trial', 'start', 'premium'],
  advancedTraining: ['premium'],
  diaryCourse: ['premium'],
  aiSettings: ['free_trial', 'premium'],
};

export function tierAllows(feature: LicenseFeature, type: LicenseType): boolean {
  return FEATURE_TIERS[feature].includes(type);
}

/**
 * Why a control is locked, naming the tiers that would unlock it rather than PREMIUM by default —
 * a trial school reaching for the ŠVP document is being sent to START, not upwards.
 *
 * The trial is never named: it is a way to try the application out, not something a school can be
 * sold, so offering it to a paying START school would be an upgrade they cannot act on. Every
 * feature above keeps PREMIUM, so removing it never empties the list.
 */
export function featureTierLabels(feature: LicenseFeature): string[] {
  return FEATURE_TIERS[feature]
    .filter((type) => type !== 'free_trial')
    .map((type) => LICENSE_TYPE_LABELS[type]);
}

export function featureTooltip(feature: LicenseFeature): string {
  const labels = featureTierLabels(feature);
  const named =
    labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(', ')} a ${labels.at(-1)}`;
  return `Tato funkce je součástí licence ${named}`;
}
